"use client"

import { ChangeTrend } from "@/app/collections/leaderboard/view/change-trend"
import { useCollectionStatitstic } from "@/app/collections/statistic/use-floor"
import { DesktopSlidePreview } from "@/app/components/slider/desktop-slide-preview"
import { MobileSlidePreview } from "@/app/components/slider/mobile-slide-preview"
import { PriceInUsd } from "@/app/price"
import { isNavigableLink } from "@/app/utils/navigable-link"
import { joinAddress } from "@anaconda/core/blockchain/address"
import { DropPreviewType } from "@anaconda/core/drop/types"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import { Button, cn } from "@rarible_int/anaconda_ui_components"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useState } from "react"

interface SlideItem {
  id: string
  title: string
  description?: string
  backgroundImage?: string
  collectionImage?: string
  collectionId?: string
  blockchain?: string // Optional blockchain for the slide
  metadata?: Record<string, unknown> // For price info, etc.
  ctaText?: string // CTA button text
  ctaLink?: string // CTA button link
}

interface SliderSectionProps {
  title?: string
  description?: string
  slides: SlideItem[]
}

interface SliderSectionComponentProps {
  props: SliderSectionProps
  styles?: {
    backgroundColor?: string
    textColor?: string
    padding?: string
    margin?: string
  }
  merchantBlockchain?: string // Fallback blockchain for slides
}

// Convert our slide format to match the exact SlideType used by spotlight
interface SlideType {
  background: string
  preview?: string
  previewType?: DropPreviewType
  name: string
  title: React.ReactNode
  subtitle: React.ReactNode
  link: string
  imageWithPriority?: boolean
}

export function SliderSection({
  props,
  styles,
  merchantBlockchain,
}: SliderSectionComponentProps) {
  const { title, description, slides } = props
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [sliderActive, setSliderActive] = useState(false)
  const slideDurationInSeconds = 5

  // Convert our slides to match the exact format expected by spotlight slider
  const convertedSlides: SlideType[] = slides.map((slide) => {
    // Build URL for blockchain/collections/collectionId
    const getSlideUrl = () => {
      if (slide.collectionId) {
        const blockchain =
          slide.blockchain?.toLowerCase() ||
          merchantBlockchain?.toLowerCase() ||
          "ethereum" // fallback to merchant blockchain, then ethereum
        return `/${blockchain}/collections/${slide.collectionId}`
      }
      return "#"
    }

    return {
      background: slide.backgroundImage || "",
      preview: slide.collectionImage || slide.backgroundImage,
      previewType: DropPreviewType.Image,
      name: slide.title,
      title: (
        <SlideTitle slide={slide} merchantBlockchain={merchantBlockchain} />
      ),
      subtitle: <SlideSubtitle slide={slide} />,
      link: getSlideUrl(),
      imageWithPriority: false,
    }
  })

  // Start the slider on mount during client-side hydration
  useEffect(() => setSliderActive(true), [])

  useEffect(() => {
    if (!sliderActive) return

    const timeout = setTimeout(() => {
      setActiveSlideIndex((activeSlideIndex + 1) % convertedSlides.length)
    }, slideDurationInSeconds * 1000)

    return () => clearTimeout(timeout)
  }, [activeSlideIndex, convertedSlides.length, sliderActive])

  if (convertedSlides.length === 0) {
    return (
      <div
        className="rounded-lg bg-card p-6"
        style={{
          backgroundColor: styles?.backgroundColor,
          color: styles?.textColor,
          padding: styles?.padding,
          margin: styles?.margin,
        }}
      >
        <div className="py-12 text-center text-foreground/50">
          <p>No slides to display</p>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        backgroundColor: styles?.backgroundColor,
        color: styles?.textColor,
        padding: styles?.padding,
        margin: styles?.margin,
      }}
    >
      {/* Section Header */}
      {(title || description) && (
        <div className="mb-6 text-center md:text-left">
          {title && <h3 className="mb-2 font-bold text-2xl">{title}</h3>}
          {description && <p className="text-foreground/70">{description}</p>}
        </div>
      )}

      {/* Exact Slider Container from spotlight */}
      <SliderContainer>
        <div
          className="flex transition-transform duration-500"
          style={{
            transform: `translateX(-${activeSlideIndex * 100}%)`,
          }}
        >
          {convertedSlides.map((slide, index) => (
            <Slide
              key={slide.name}
              slide={slide}
              originalSlide={slides[index]}
              merchantBlockchain={merchantBlockchain}
            />
          ))}
        </div>
        <div className="absolute right-0 bottom-0 hidden h-[168px] items-end gap-4 p-4 lg:flex lg:p-6">
          {convertedSlides.map((slide, index) => (
            <DesktopSlidePreview
              timerDurationInSeconds={slideDurationInSeconds}
              onClick={() => setActiveSlideIndex(index)}
              key={slide.name}
              slideActive={index === activeSlideIndex}
              withTimer={convertedSlides.length > 1 && sliderActive}
              mediaSrc={slide.preview ?? slide.background}
              mediaType={slide.previewType}
              title={slide.name}
            />
          ))}
        </div>
        <div className="flex items-end justify-center gap-2 lg:hidden">
          {convertedSlides.map((slide, index) => (
            <MobileSlidePreview
              timerDurationInSeconds={slideDurationInSeconds}
              onClick={() => setActiveSlideIndex(index)}
              key={slide.name}
              slideActive={index === activeSlideIndex}
              withTimer={sliderActive}
            />
          ))}
        </div>
      </SliderContainer>
    </div>
  )
}

// Exact SliderContainer from spotlight
function SliderContainer({
  children,
  className,
}: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "relative flex flex-col gap-4 overflow-hidden lg:rounded-xl lg:border lg:border-foreground/10",
        className,
      )}
    >
      {children}
    </div>
  )
}

// Exact Slide component from spotlight
function Slide({
  slide,
  originalSlide,
  merchantBlockchain,
}: {
  slide: SlideType
  originalSlide: SlideItem
  merchantBlockchain?: string
}) {
  return (
    <div className="relative w-full flex-shrink-0">
      <Link href={slide.link} className="absolute inset-0 z-10" />
      <SlideContainer>
        <Image
          className="object-cover"
          fill
          src={slide.background}
          alt={`${slide.name} background`}
          unoptimized
          priority={slide.imageWithPriority}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60" />
        {/* Full screen gradient for mobile readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/80 md:hidden" />
        <div className="absolute right-0 bottom-0 left-0 z-20 flex w-full flex-col gap-4 p-6 lg:p-8">
          {/* Title and Description */}
          <div className="flex w-full flex-col">
            <Link href={slide.link}>
              <SlideTitle
                slide={originalSlide}
                merchantBlockchain={merchantBlockchain}
              />
            </Link>
            <SlideSubtitle slide={originalSlide} />
          </div>

          {/* Stats and Button in the same section */}
          <div className="flex w-full flex-wrap items-center justify-start gap-6">
            <SlideCTAButton slide={originalSlide} />
            <SlideStats
              slide={originalSlide}
              merchantBlockchain={merchantBlockchain}
            />
          </div>
        </div>
      </SlideContainer>
    </div>
  )
}

// Exact SlideContainer from spotlight
function SlideContainer({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "relative aspect-[21/5] min-h-[300px] w-full overflow-hidden rounded-xl",
      )}
    >
      {children}
    </div>
  )
}

// Component to display slide title only
function SlideTitle({
  slide,
}: { slide: SlideItem; merchantBlockchain?: string }) {
  return (
    <h1
      className="w-full break-words font-bold text-2xl leading-tight md:text-4xl lg:text-5xl xl:text-6xl"
      title={slide.title}
    >
      {slide.title}
    </h1>
  )
}

// Component to display slide stats separately
function SlideStats({
  slide,
  merchantBlockchain,
}: { slide: SlideItem; merchantBlockchain?: string }) {
  const hasValidCollectionId = Boolean(slide.collectionId?.trim())

  // Construct proper CollectionId with blockchain prefix
  const getCollectionId = (): CollectionId => {
    if (!slide.collectionId) return "" as CollectionId

    // If already has blockchain prefix (contains ":"), use as-is
    if (slide.collectionId.includes(":")) {
      return slide.collectionId as CollectionId
    }

    // Otherwise, add blockchain prefix
    const blockchain =
      slide.blockchain?.toUpperCase() ||
      merchantBlockchain?.toUpperCase() ||
      "ETHEREUM"
    return joinAddress(blockchain, slide.collectionId) as CollectionId
  }

  const collectionId = getCollectionId()
  const {
    data: collectionStats,
    error,
    isLoading,
  } = useCollectionStatitstic(collectionId)

  // Only display stats if we have valid collection ID and data
  if (!hasValidCollectionId || !collectionStats) {
    return null
  }

  return (
    <div className="flex items-center gap-x-6 text-sm">
      {/* Floor Price */}
      {collectionStats.floor && (
        <div className="flex flex-col">
          <span className="text-foreground/80 text-xs">Floor Price</span>
          <PriceInUsd {...collectionStats.floor} className="text-white" />
        </div>
      )}

      {/* 24h Change - only show if not zero */}
      {collectionStats.dayFloorChange != null &&
        typeof collectionStats.dayFloorChange === "number" &&
        !Number.isNaN(collectionStats.dayFloorChange) &&
        collectionStats.dayFloorChange !== 0 &&
        Math.abs(collectionStats.dayFloorChange) > 0.00001 && (
          <div className="flex flex-col">
            <span className="text-foreground/80 text-xs">24h Change</span>
            <ChangeTrend changePercent={collectionStats.dayFloorChange} />
          </div>
        )}

      {/* Items Count (if available) */}
      {collectionStats.items && (
        <div className="flex flex-col">
          <span className="text-foreground/80 text-xs">Items</span>
          <span className="text-white">
            {collectionStats.items.toLocaleString()}
          </span>
        </div>
      )}
    </div>
  )
}

// Component to display slide subtitle
function SlideSubtitle({ slide }: { slide: SlideItem }) {
  return slide.description ? (
    <p className="mt-2 max-w-2xl text-foreground/80 text-xs md:text-sm lg:text-base">
      {slide.description}
    </p>
  ) : null
}

// Component to display CTA button only when both ctaText and ctaLink are provided
function SlideCTAButton({ slide }: { slide: SlideItem }) {
  // Only render if both ctaText and ctaLink are provided and not empty
  if (!slide.ctaText?.trim() || !slide.ctaLink?.trim()) {
    return null
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    // The link comes from merchant content: never navigate to `javascript:`.
    if (!slide.ctaLink || !isNavigableLink(slide.ctaLink)) {
      return
    }
    window.location.href = slide.ctaLink
  }

  return (
    <Button
      onClick={handleClick}
      variant="default"
      className="relative z-30 rounded-full border-none bg-white p-6 text-center font-semibold text-black text-lg leading-6"
      style={{
        fontSize: "18px",
        fontWeight: 600,
        lineHeight: "24px",
      }}
    >
      {slide.ctaText}
    </Button>
  )
}
